/**
 * Trim ArtifactMetadataEngine's boot load to Artifact Types only (MJ 5.40.2).
 *
 * MJ's ArtifactMetadataEngine.Config() eager-loads THREE entities with
 * CacheLocal and no field restriction: Artifact Types, Artifacts, and
 * Artifact Versions — the last including the full `Content` column for every
 * version system-wide. Izzy stores email-attachment binaries as base64 in
 * ArtifactVersion.Content, so on prod that is ~244MB (127 versions) shipped
 * on EVERY boot (~51MB gzipped): 7.5s of server serialize + 4.7s download —
 * the dominant cause of prod's ~12s boots. Worse, caching the result
 * estimates ~500MB against the 150MB cache budget, so LocalCacheManager's
 * eviction WIPES THE ENTIRE IndexedDB cache every boot trying to make room
 * (and the oversized write never sticks) — which is the primary trigger of
 * the smart-cache TOCTOU failures ("Cache marked current but no cached data
 * found") and the fail-open Home-button leak.
 *
 * The engine's own pre-warm call site documents the original intent:
 * "ArtifactMetadataEngine is lightweight (just artifact types)"
 * (ng-shared shared.service.js). This patch restores exactly that: load only
 * 'MJ: Artifact Types' (a tiny catalog used for MIME resolution). Verified
 * consumers: NOTHING in Izzy source, any shipped ng-* package, or
 * react-runtime reads engine.Artifacts / engine.ArtifactVersions — artifact
 * viewers fetch records directly on open.
 *
 * Kill switch: ?mjPatchDisable=1 or localStorage.mjPatchDisable='1'.
 * Remove when MJ ships an upstream fix (lazy/metadata-only version loading).
 */
import { BaseEnginePropertyConfig, IMetadataProvider, UserInfo } from '@memberjunction/core';
import { ArtifactMetadataEngine } from '@memberjunction/core-entities';

const ARTIFACT_PATCH_DISABLED =
  new URLSearchParams(window.location.search).get('mjPatchDisable') === '1' ||
  window.localStorage.getItem('mjPatchDisable') === '1';

if (ARTIFACT_PATCH_DISABLED) {
  console.warn('[ArtifactBootLoadTrim] DISABLED via mjPatchDisable flag');
}

if (!ARTIFACT_PATCH_DISABLED) ArtifactMetadataEngine.prototype.Config = async function (
  this: ArtifactMetadataEngine,
  forceRefresh?: boolean,
  contextUser?: UserInfo,
  provider?: IMetadataProvider
): Promise<void> {
  const configs: Partial<BaseEnginePropertyConfig>[] = [
    {
      Type: 'entity',
      EntityName: 'MJ: Artifact Types',
      PropertyName: '_artifactTypes',
      CacheLocal: true,
    },
  ];
  // Load is protected on BaseEngine; element access is the typed escape hatch
  // (no `as any`). Argument order matches the engine's own Config: (configs,
  // provider, forceRefresh, contextUser).
  await this['Load'](configs, provider as IMetadataProvider, forceRefresh, contextUser);
};
